import React from 'react';
import { H1, H3, Button, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import {
  AttentionItem,
  ShipmentFunnnelStep,
  RouteInsight,
  RiskSignal,
  OrderExceptionEntry,
} from './types';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatChange,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  OrderId,
  StatusContainer,
  Amount,
  DashboardGrid,
  FullWidthSection,
  SecondarySection,
} from './styles';
import { AttentionRequiredStrip } from './AttentionRequiredStrip';
import { ShipmentFunnel } from './ShipmentFunnel';
import { RouteInsights } from './RouteInsights';
import { RiskSignals } from './RiskSignals';
import { getStatusIcon } from './statusIcon';

const OPS_DASHBOARD_QUERY = gql`
  query OpsDashboard {
    dashboardMetrics {
      totalShipments
      activeShipments
    }
    attentionItems {
      label
      count
      priority
    }
    shipmentFunnel {
      stage
      count
      avgTime
      slaWarning
    }
    routeInsights {
      route
      deliveryTime
      cost
      delayRisk
      trend
    }
    riskSignals {
      label
      status
    }
    recentExceptions {
      source
      count
      entries {
        orderId
        shipmentNo
        customerName
        status
        exceptionReason
        amount
      }
    }
  }
`;

interface OpsDashboardProps {
  onViewOperationsHub?: () => void;
  onOrderClick?: (orderId: string) => void;
}

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const OpsDashboard: React.FC<OpsDashboardProps> = ({ onViewOperationsHub, onOrderClick }) => {
  const { data } = useGraphQLQuery<{
    dashboardMetrics: { totalShipments: number; activeShipments: number };
    attentionItems: Array<{ label: string; count: number; priority: string }>;
    shipmentFunnel: Array<{ stage: string; count: number; avgTime?: string; slaWarning?: string }>;
    routeInsights?: Array<{ route: string; deliveryTime: string; cost: string; delayRisk: RouteInsight['delayRisk']; trend: RouteInsight['trend'] }>;
    riskSignals?: Array<{ label: string; status: RiskSignal['status'] }>;
    recentExceptions?: { source: string; count: number; entries: OrderExceptionEntry[] };
  }>(OPS_DASHBOARD_QUERY, {});

  const metrics = data?.dashboardMetrics;
  const attentionItemsLive: AttentionItem[] = data?.attentionItems?.map((a) => ({ label: a.label, count: a.count, priority: a.priority as any })) ?? [];
  const funnelStepsLive: ShipmentFunnnelStep[] = data?.shipmentFunnel?.map((s) => ({ stage: s.stage, count: s.count, avgTime: s.avgTime ?? undefined, slaWarning: s.slaWarning ?? undefined })) ?? [];
  const routeInsightsLive: RouteInsight[] = data?.routeInsights?.map((r) => ({ route: r.route, deliveryTime: r.deliveryTime, cost: r.cost, delayRisk: r.delayRisk, trend: r.trend })) ?? [];
  const riskSignalsLive: RiskSignal[] = data?.riskSignals?.map((s) => ({ label: s.label, status: s.status })) ?? [];
  const entries = data?.recentExceptions?.entries ?? [];

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>Logistics & Execution</H1>
          <Subtitle>Shipment flow, delivery risk, and exceptions that need action today.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Total Shipments</StatTitle>
              <StatValue>{metrics?.totalShipments ?? '—'}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Package" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Active Shipments</StatTitle>
              <StatValue>{metrics?.activeShipments ?? '—'}</StatValue>
              <StatChange><Icon name="TrendingUp" /><span>Live</span></StatChange>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Truck" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <AttentionRequiredStrip items={attentionItemsLive} />

      <DashboardGrid>
        <ShipmentFunnel steps={funnelStepsLive} />

        <FullWidthSection>
          <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <OrdersHeader>
              <H3 style={{ margin: '0' }}>Recent Exceptions / High Priority Shipments</H3>
              <Button variant="link" style={{ padding: 0 }} onClick={onViewOperationsHub}>View Operations Hub</Button>
            </OrdersHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Customer</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Exception Reason</TableHeader>
                    <TableHeader>Amount</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {entries.map((row, index) => (
                    <TableRow key={`${row.orderId}-${index}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * index }}>
                      <TableCell><OrderId style={{ cursor: 'pointer', color: colors.primary }} onClick={() => onOrderClick?.(row.orderId)}>{row.orderId ?? '—'}</OrderId></TableCell>
                      <TableCell>{row.customerName ?? '—'}</TableCell>
                      <TableCell><StatusContainer>{getStatusIcon(row.status ?? 'Pending')}<span>{row.status ?? '—'}</span></StatusContainer></TableCell>
                      <TableCell><span style={{ color: colors.error, fontWeight: 600, fontSize: '0.75rem' }}>{row.exceptionReason ?? '—'}</span></TableCell>
                      <TableCell><Amount>{formatAmount(row.amount)}</Amount></TableCell>
                    </TableRow>
                  ))}
                  {entries.length === 0 && (
                    <TableRow initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                      <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No exception records</span></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </OrdersCard>
        </FullWidthSection>

        <SecondarySection style={{ gridColumn: '1 / -1' }}>
          <RouteInsights insights={routeInsightsLive} />
          <RiskSignals signals={riskSignalsLive} />
        </SecondarySection>
      </DashboardGrid>
    </DashboardContainer>
  );
};
